import React, {useEffect, useState} from 'react';
import {fontObserver} from '../helper/font';
import sanity, {imageUrl} from '../lib/sanity';
import Page from '../components/page';
import Header from '../components/header';
import {ContentSection} from '../components/section';
import Content from '../components/content';
import Slider from '../components/slider';
import {H2} from '../components/webComponents';
import Footer from '../components/footer';
import SeoHeader from '../components/seoHeader';
import {useFormik} from 'formik';
import * as Yup from 'yup';
import {Form, Input, Button, TextArea} from '../components/input';
import Grid from 'styled-components-grid';
import styled from 'styled-components';
import postData from '../helper/postData';
import {FormSuccess, FormError, FormSending} from '../components/formComponents';
import * as gtag from '../lib/gtag';

const Row = styled(Grid)`
  width: calc(100% + ${({theme}) => theme.spacing.xsmall} + ${({theme}) => theme.spacing.xsmall});
  margin: 0 -${({theme}) => theme.spacing.xsmall};
`;

const Column = styled(Grid.Unit)`
  padding: 0 ${({theme}) => theme.spacing.xsmall};
`;

const pageQuery = `*[_type == "page" && slug.current=="kontakt"] {
  _id,
  title,
  slug,
  description,
  image,
  content,
  tabs
}[0]`;

const productCategoriesQuery = `*[_type == "productCategory"] {
    _id,
    title,
    slug,
    image,
    "products": *[_type == "product" && _id in ^.products[]._ref]
  }`;

const Home = ({page, productCategories}) => {
  useEffect(() => {
    fontObserver();
  }, []);
  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);
  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      message: '',
    },
    validationSchema: Yup.object().shape({
      firstName: Yup.string().min(2, 'zu kurz').max(50, 'zu lang').required('Pflichtfeld'),
      lastName: Yup.string().min(2, 'zu kurz').max(50, 'zu lang').required('Pflichtfeld'),
      email: Yup.string().email('ungültige Email').required('Pflichtfeld'),
      phone: Yup.string().min(4, 'zu kurz').max(20, 'zu lang').required('Pflichtfeld'),
      message: Yup.string().min(2, 'zu kurz').required('Pflichtfeld'),
    }),
    enableReinitialize: true,
    onSubmit: (values) => {
      gtag.event({
        category: 'form',
        action: 'submit',
        label: 'sending contact form',
      });
      formik.setSubmitting(true);
      postData('/api/mail', {...values})
        .then(() => {
          formik.setSubmitting(false);
          formik.resetForm();
          setError(false);
          setSuccess(true);
        })
        .catch(() => {
          formik.setSubmitting(false);
          setSuccess(false);
          setError(true);
        });
    },
  });
  return (
    <Page>
      <SeoHeader
        title={page?.title}
        description={page?.description}
        image={imageUrl(page?.image, 80, 600)}
        url={'/kontakt'}
      />
      <Header
        title={page?.title}
        description={page?.description}
        placeholderImage={imageUrl(page?.image, 10, 10)}
        largeImage={imageUrl(page?.image, 80, 1000)}
      />
      <ContentSection>{page?.content && <Content content={page?.content} />}</ContentSection>
      <ContentSection>
        <Form onSubmit={formik.handleSubmit} autocomplete='off'>
          <Row>
            <Column size={1}>
              {formik.isSubmitting && <FormSending>Nachricht wird gesendet...</FormSending>}
              {!formik.isSubmitting && error && (
                <FormError>Es ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut.</FormError>
              )}
              {!formik.isSubmitting && success && (
                <FormSuccess>Ihre Nachricht wurde erfolgreich gesendet. Wir melden uns bei Ihnen.</FormSuccess>
              )}
            </Column>
            <Column size={{xs: 1, md: 1 / 2}}>
              <Input
                id='firstName'
                name='firstName'
                type='text'
                placeholder='Vorname'
                handleChange={(e) => {
                  formik.handleChange(e);
                  setError(false);
                  setSuccess(false);
                }}
                setFieldTouched={formik.setFieldTouched}
                value={formik.values.firstName}
                error={formik.errors.firstName}
                touched={formik.touched.firstName}
              />
            </Column>
            <Column size={{xs: 1, md: 1 / 2}}>
              <Input
                id='lastName'
                name='lastName'
                placeholder='Nachname'
                type='text'
                handleChange={(e) => {
                  formik.handleChange(e);
                  setError(false);
                  setSuccess(false);
                }}
                setFieldTouched={formik.setFieldTouched}
                value={formik.values.lastName}
                error={formik.errors.lastName}
                touched={formik.touched.lastName}
              />
            </Column>
            <Column size={{xs: 1, md: 1 / 2}}>
              <Input
                id='email'
                name='email'
                type='email'
                placeholder='Email'
                handleChange={(e) => {
                  formik.handleChange(e);
                  setError(false);
                  setSuccess(false);
                }}
                setFieldTouched={formik.setFieldTouched}
                value={formik.values.email}
                error={formik.errors.email}
                touched={formik.touched.email}
              />
            </Column>
            <Column size={{xs: 1, md: 1 / 2}}>
              <Input
                id='phone'
                name='phone'
                type='phone'
                placeholder='Telefon'
                handleChange={(e) => {
                  formik.handleChange(e);
                  setError(false);
                  setSuccess(false);
                }}
                setFieldTouched={formik.setFieldTouched}
                value={formik.values.phone}
                error={formik.errors.phone}
                touched={formik.touched.phone}
              />
            </Column>
            <Column size={{xs: 1}}>
              <TextArea
                id='message'
                name='message'
                type='message'
                placeholder='Nachricht'
                handleChange={(e) => {
                  formik.handleChange(e);
                  setError(false);
                  setSuccess(false);
                }}
                setFieldTouched={formik.setFieldTouched}
                value={formik.values.message}
                error={formik.errors.message}
                touched={formik.touched.message}
              />
            </Column>
            <Column size={{xs: 1, md: 1 / 2}}>
              <Button type='submit'>senden</Button>
            </Column>
          </Row>
        </Form>
      </ContentSection>
      {productCategories?.length > 0 && (
        <ContentSection>
          <H2>Produkt-Kategorien</H2>
          <Slider
            grid
            items={productCategories?.map((item) => ({
              line1: item?.title,
              line2: `${item?.products?.length} ${item?.products?.length === 1 ? `Produkt` : `Produkte`}`,
              image: imageUrl(item?.image, 80, 500),
              as: `/produkt-kategorie/${item?.slug?.current}`,
              link: `/produkt-kategorie/[slug]`,
            }))}
          />
        </ContentSection>
      )}
      <Footer />
    </Page>
  );
};

export const getStaticProps = async () => {
  const page = await sanity.fetch(pageQuery);
  const productCategories = await sanity.fetch(productCategoriesQuery);
  return {
    props: {
      page,
      productCategories,
    },
    revalidate: 10,
  };
};

export default Home;
